import React, { useMemo } from 'react'

import ReactFlow, { Background, Controls, Edge, EdgeProps, Node, NodeProps, Position, getBezierPath } from 'reactflow'
import 'reactflow/dist/style.css'

import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useTokensStore } from '@src/zustand_stores'

// Custom edge component with arrow
const CustomEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style: _,
    data,
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */ markerEnd,
}: EdgeProps) => {
    // Use getBezierPath for a curved edge path
    const [edgePath] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition: sourcePosition || Position.Right,
        targetX,
        targetY,
        targetPosition: targetPosition || Position.Left,
        curvature: 0.2, // Add slight curvature for better visibility
    })

    // Calculate the midpoint for the label
    const midX = (sourceX + targetX) / 2
    const midY = (sourceY + targetY) / 2 - 10

    // Arrow styling
    const arrowSize = 10
    const arrowColor = '#00FF00'

    return (
        <g>
            {/* Main path - thicker for better visibility */}
            <path
                id={id}
                className='react-flow__edge-path'
                d={edgePath}
                strokeWidth={3}
                stroke={arrowColor}
                fill='none'
                strokeLinecap='round'
            />

            {/* Animated overlay */}
            <path
                className='react-flow__edge-animated'
                d={edgePath}
                strokeWidth={3}
                stroke={arrowColor}
                fill='none'
                strokeDasharray='5,5'
                strokeLinecap='round'
            >
                <animate attributeName='stroke-dashoffset' from='0' to='-10' dur='1s' repeatCount='indefinite' />
            </path>

            {/* Arrow head - larger and more visible */}
            <polygon
                points={`${targetX},${targetY} ${targetX - arrowSize},${targetY - arrowSize} ${targetX - arrowSize},${targetY + arrowSize}`}
                fill={arrowColor}
                stroke={arrowColor}
            />

            {/* Label with background */}
            {data?.label && (
                <>
                    <rect
                        x={midX - 40}
                        y={midY - 10}
                        width='80'
                        height='20'
                        rx='4'
                        fill='#1A1A1A'
                        fillOpacity='0.8'
                        stroke='#333333'
                        strokeWidth='1'
                    />
                    <text
                        x={midX}
                        y={midY + 5}
                        textAnchor='middle'
                        style={{ fill: '#00FF00', fontSize: 12, fontWeight: 500 }}
                    >
                        {data.label}
                    </text>
                </>
            )}
        </g>
    )
}

interface JupiterRouteModalProps {
    isOpen: boolean
    onClose: () => void
}

interface RouteNode {
    id: string
    tokenSymbol: string
    tokenMint: string
    amount: string
    decimals: number
    isInput?: boolean
    isOutput?: boolean
}

// No longer needed as we're using Edge type directly

export const JupiterRouteModal: React.FC<JupiterRouteModalProps> = ({ isOpen, onClose }) => {
    // Get the Jupiter quote from the exchange data context
    const { quoteResponse } = useExchangeDataContext()
    const tokens = useTokensStore(state => state.tokens)

    // Get Jupiter quote safely
    const jupiterQuote = quoteResponse?.quote?.jupiter_quote

    // Get token metadata from mint address using token store
    const getTokenMetadata = (mintAddress: string) => {
        // Try to find in token store first
        const token = tokens.find(t => t.address.toLowerCase() === mintAddress.toLowerCase())

        return {
            symbol: token
                ? token.symbol
                : `${mintAddress.substring(0, 4)}...${mintAddress.substring(mintAddress.length - 4)}`,
            logo: token?.logoURI || '',
            decimals: token?.decimals || 9, // Default to 9 decimals (common for Solana tokens) if not found
        }
    }

    // Helper function to format token amounts
    const formatTokenAmount = (amount: string, decimals: number): string => {
        if (!amount) return '0'

        // Convert to number with proper decimal places
        const value = parseFloat(amount) / Math.pow(10, decimals)

        // Format based on size
        if (value > 1000000) {
            return (value / 1000000).toFixed(2) + 'M'
        } else if (value > 1000) {
            return (value / 1000).toFixed(2) + 'K'
        } else if (value < 0.001 && value > 0) {
            return '<0.001'
        } else {
            return value.toFixed(value < 1 ? 4 : 2)
        }
    }

    // Create route summary string - defined before early return to avoid conditional hook calls
    const routeSummary = useMemo(() => {
        if (!jupiterQuote?.routePlan || jupiterQuote.routePlan.length === 0) return ''

        // Get all tokens in the route
        const tokenSymbols = []
        tokenSymbols.push(getTokenMetadata(jupiterQuote.routePlan[0].swapInfo.inputMint).symbol)

        jupiterQuote.routePlan.forEach(route => {
            tokenSymbols.push(getTokenMetadata(route.swapInfo.outputMint).symbol)
        })

        // Create route string (e.g., "USDC → JUP → SOL")
        return tokenSymbols.join(' → ')
    }, [jupiterQuote, getTokenMetadata])

    // Build graph data for ReactFlow visualization
    const { flowNodes, flowEdges } = useMemo(() => {
        const flowNodes: Node[] = []
        const flowEdges: Edge[] = []

        if (!jupiterQuote?.routePlan || jupiterQuote.routePlan.length === 0 || !isOpen) {
            return { flowNodes, flowEdges }
        }

        // Set up positions and spacing
        let xPosition = 50
        const yPosition = 80
        const horizontalSpacing = 200

        // Process the route plan to create a clean flow
        const routePlan = jupiterQuote.routePlan

        // Add the first input token
        const firstRoute = routePlan[0]
        const inputMint = firstRoute.swapInfo.inputMint
        const inputMetadata = getTokenMetadata(inputMint)

        // Add input node to flow
        flowNodes.push({
            id: 'input-node',
            type: 'tokenNode',
            position: { x: xPosition, y: yPosition },
            data: {
                tokenSymbol: inputMetadata.symbol,
                tokenMint: inputMint,
                amount: firstRoute.swapInfo.inAmount,
                decimals: inputMetadata.decimals,
                isInput: true,
            },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
        })

        // Track the last node ID to connect edges
        let lastNodeId = 'input-node'

        // Process each hop in the route
        routePlan.forEach((route, index) => {
            const { swapInfo } = route
            const { label, outputMint } = swapInfo
            const outputMetadata = getTokenMetadata(outputMint)
            const isLastToken = index === routePlan.length - 1

            // Update x position for the next node
            xPosition += horizontalSpacing

            // Create node ID
            const nodeId = `node-${index}`

            // Add token node
            flowNodes.push({
                id: nodeId,
                type: 'tokenNode',
                position: { x: xPosition, y: yPosition },
                data: {
                    tokenSymbol: outputMetadata.symbol,
                    tokenMint: outputMint,
                    amount: swapInfo.outAmount,
                    decimals: outputMetadata.decimals,
                    isOutput: isLastToken,
                },
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
            })

            // Add edge connecting to the previous node
            flowEdges.push({
                id: `edge-${index}`,
                source: lastNodeId,
                target: nodeId,
                type: 'custom', // Use our custom edge type
                data: { label: label },
                style: { stroke: '#00FF00', strokeWidth: 3 },
            })

            // Update last node ID for the next iteration
            lastNodeId = nodeId
        })

        return { flowNodes, flowEdges }
    }, [jupiterQuote, isOpen, getTokenMetadata])

    // Early return if modal is not open or no quote data
    if (!isOpen || !jupiterQuote) {
        return null
    }

    return (
        <div
            className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80'
            onClick={onClose}
        >
            <div
                className='h-[90vh] w-[95vw] max-w-[1200px] overflow-auto rounded-lg bg-mach-modal-background p-6'
                onClick={e => e.stopPropagation()}
            >
                <div className='mb-6 flex items-center justify-between'>
                    <h3 className='text-2xl font-bold text-white'>Jupiter Swap Route Details</h3>
                    <button
                        className='rounded-full bg-mach-surface-background p-2 text-mach-sub-text transition-colors duration-200 hover:text-white'
                        onClick={onClose}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-6 w-6'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M6 18L18 6M6 6l12 12'
                            />
                        </svg>
                    </button>
                </div>

                {/* Route summary at the top of the modal */}
                <div className='mb-8 rounded-md bg-mach-gray p-4'>
                    <div className='text-center text-xl font-medium text-white'>{routeSummary}</div>
                </div>

                {/* ReactFlow chart visualization */}
                <div className='mb-8'>
                    <h4 className='mb-4 text-lg font-medium text-white'>Route Visualization</h4>
                    <div className='relative overflow-hidden rounded-lg bg-mach-gray' style={{ height: '250px' }}>
                        <ReactFlow
                            nodes={flowNodes}
                            edges={flowEdges}
                            fitView
                            fitViewOptions={{ padding: 0.2 }}
                            attributionPosition='bottom-left'
                            defaultEdgeOptions={{ animated: true }}
                            edgeTypes={{ custom: CustomEdge }}
                            nodeTypes={{
                                tokenNode: ({ data }: NodeProps<RouteNode>) => (
                                    <div
                                        className={`min-w-[130px] rounded-lg p-4 ${data.isInput || data.isOutput ? 'bg-mach-green bg-opacity-20' : 'bg-mach-surface-background'} border-2 ${data.isInput ? 'border-mach-green' : data.isOutput ? 'border-mach-green' : 'border-mach-gray'} shadow-lg`}
                                    >
                                        {/* Token logo and symbol */}
                                        <div className='mb-3 flex items-center gap-2'>
                                            {getTokenMetadata(data.tokenMint).logo && (
                                                <img
                                                    src={getTokenMetadata(data.tokenMint).logo || ''}
                                                    alt={data.tokenSymbol}
                                                    className='h-7 w-7 rounded-full shadow-sm'
                                                />
                                            )}
                                            <span className='text-lg font-medium text-white'>{data.tokenSymbol}</span>
                                        </div>
                                        {/* Amount */}
                                        <div className='text-sm font-medium text-mach-sub-text'>
                                            {formatTokenAmount(data.amount, data.decimals)}
                                        </div>
                                        {/* Input/Output indicator */}
                                        {(data.isInput || data.isOutput) && (
                                            <div
                                                className={`absolute -right-2 -top-2 rounded-full px-3 py-1 text-xs font-bold ${data.isInput ? 'bg-mach-green text-black' : 'bg-mach-green text-black'} shadow-md`}
                                            >
                                                {data.isInput ? 'Input' : 'Output'}
                                            </div>
                                        )}
                                    </div>
                                ),
                            }}
                        >
                            <Background color='#333' gap={16} />
                            <Controls showInteractive={false} position='bottom-right' />
                        </ReactFlow>
                    </div>
                </div>

                {/* Pool details in a cleaner card layout */}
                <div className='border-t border-mach-gray pt-6'>
                    <h4 className='mb-4 text-lg font-medium text-white'>Pool Details</h4>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
                        {jupiterQuote?.routePlan.map((route, index) => (
                            <div
                                key={`details-${index}`}
                                className='rounded-lg border border-mach-gray bg-mach-surface-background p-4 shadow-lg transition-shadow duration-200 hover:shadow-xl'
                            >
                                <div className='mb-3 flex items-center justify-between border-b border-mach-gray pb-2'>
                                    <div className='flex items-center gap-2'>
                                        <div className='flex h-6 w-6 items-center justify-center rounded-full bg-mach-green font-medium text-black'>
                                            {index + 1}
                                        </div>
                                        <span className='font-medium text-white'>Pool #{index + 1}</span>
                                    </div>
                                    <span className='font-bold text-mach-green'>{route.swapInfo.label}</span>
                                </div>

                                <div className='space-y-3'>
                                    {/* Token flow with enhanced arrow visualization */}
                                    <div className='relative my-3 flex items-center justify-between'>
                                        <div className='z-10 flex items-center gap-2'>
                                            {getTokenMetadata(route.swapInfo.inputMint).logo && (
                                                <img
                                                    src={getTokenMetadata(route.swapInfo.inputMint).logo}
                                                    alt={getTokenMetadata(route.swapInfo.inputMint).symbol}
                                                    className='h-6 w-6 rounded-full border border-mach-gray'
                                                />
                                            )}
                                            <span className='font-medium text-white'>
                                                {getTokenMetadata(route.swapInfo.inputMint).symbol}
                                            </span>
                                        </div>

                                        {/* Animated arrow with label */}
                                        <div className='pointer-events-none absolute left-0 right-0 flex items-center justify-center'>
                                            <div className='flex items-center gap-2 rounded-full border border-mach-gray bg-mach-surface-background px-3 py-1'>
                                                <div className='text-xs text-mach-green'>{route.swapInfo.label}</div>
                                                <div className='relative h-1 w-16 overflow-hidden rounded-full bg-mach-gray'>
                                                    <div className='animate-flow absolute left-0 top-0 h-full w-full bg-gradient-to-r from-transparent via-mach-green to-transparent'></div>
                                                </div>
                                                {route.percent < 100 && (
                                                    <div className='text-xs font-bold text-mach-green'>
                                                        {route.percent}%
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='z-10 flex items-center gap-2'>
                                            {getTokenMetadata(route.swapInfo.outputMint).logo && (
                                                <img
                                                    src={getTokenMetadata(route.swapInfo.outputMint).logo}
                                                    alt={getTokenMetadata(route.swapInfo.outputMint).symbol}
                                                    className='h-6 w-6 rounded-full border border-mach-gray'
                                                />
                                            )}
                                            <span className='font-medium text-white'>
                                                {getTokenMetadata(route.swapInfo.outputMint).symbol}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Amounts */}
                                    <div className='grid grid-cols-2 gap-2'>
                                        <div className='rounded-lg bg-mach-gray bg-opacity-30 p-2'>
                                            <div className='mb-1 text-xs text-mach-sub-text'>Input Amount</div>
                                            <div className='font-medium text-white'>
                                                {formatTokenAmount(
                                                    route.swapInfo.inAmount,
                                                    getTokenMetadata(route.swapInfo.inputMint).decimals
                                                )}
                                            </div>
                                        </div>
                                        <div className='rounded-lg bg-mach-gray bg-opacity-30 p-2'>
                                            <div className='mb-1 text-xs text-mach-sub-text'>Output Amount</div>
                                            <div className='font-medium text-white'>
                                                {formatTokenAmount(
                                                    route.swapInfo.outAmount,
                                                    getTokenMetadata(route.swapInfo.outputMint).decimals
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* AMM Key and Percentage */}
                                    <div className='flex items-center justify-between'>
                                        <div className='text-xs text-mach-sub-text'>
                                            AMM:{' '}
                                            <span className='font-mono'>
                                                {route.swapInfo.ammKey.substring(0, 4)}...
                                                {route.swapInfo.ammKey.substring(route.swapInfo.ammKey.length - 4)}
                                            </span>
                                        </div>
                                        <div className='rounded-full bg-mach-green px-2 py-0.5 text-xs font-medium text-black'>
                                            {route.percent}%
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
